import * as React from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { organizationApi } from '@/lib/api/services/organization.api';
import { useAuth } from '@/hooks/useAuth';
import type { Organization } from '@/types/organization';

const STORAGE_KEY = 'yeschef_active_org_id';

interface ActiveOrganizationContextValue {
  activeOrganization: Organization | null;
  setActiveOrganization: (org: Organization | null) => void;
  organizations: Organization[];
  isLoading: boolean;
}

const ActiveOrganizationContext = React.createContext<ActiveOrganizationContextValue | null>(null);

export function ActiveOrganizationProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, hasPermission, user } = useAuth();
  const canUseAdminScope = hasPermission('*:*') && !user?.isImpersonating;
  const nowIso = React.useMemo(() => new Date().toISOString(), []);
  const userOrganization = user?.organization
    ? {
        id: user.organization.id,
        name: user.organization.name,
        slug: user.organization.slug,
        isActive: true,
        createdAt: nowIso,
        updatedAt: nowIso,
      }
    : null;

  const { data, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: ['organizations', 'all'],
    queryFn: ({ pageParam }) => organizationApi.paginate({ limit: 100, page: pageParam as number }, { adminScope: true }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => lastPage.meta.hasNext ? lastPage.meta.page + 1 : undefined,
    enabled: isAuthenticated && canUseAdminScope,
    staleTime: 1000 * 60 * 5,
  });

  const pagesFetched = data?.pages.length ?? 0;

  // Auto-fetch remaining pages, capped at 20 pages (2 000 orgs at limit:100)
  React.useEffect(() => {
    if (hasNextPage && !isFetchingNextPage && pagesFetched < 20) {
      void fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, pagesFetched]);

  const organizations = React.useMemo(() => {
    if (!canUseAdminScope) {
      return userOrganization ? [userOrganization] : [];
    }
    return data?.pages.flatMap((p) => p.data) ?? [];
  }, [canUseAdminScope, data, userOrganization]);

  const [activeOrgId, setActiveOrgId] = React.useState<number | null>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? Number(stored) : null;
  });

  /* eslint-disable react-hooks/set-state-in-effect */
  React.useEffect(() => {
    if (!canUseAdminScope && userOrganization) {
      if (activeOrgId !== userOrganization.id) {
        setActiveOrgId(userOrganization.id);
      }
      if (localStorage.getItem(STORAGE_KEY) !== String(userOrganization.id)) {
        localStorage.setItem(STORAGE_KEY, String(userOrganization.id));
      }
      return;
    }

    if (canUseAdminScope && activeOrgId !== null && !organizations.some((org) => org.id === activeOrgId)) {
      setActiveOrgId(null);
      localStorage.removeItem(STORAGE_KEY);
    }
  }, [canUseAdminScope, userOrganization, activeOrgId, organizations]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const activeOrganization = organizations.find((o) => o.id === activeOrgId) ?? null;

  const setActiveOrganization = React.useCallback((org: Organization | null) => {
    setActiveOrgId(org?.id ?? null);
    if (org) {
      localStorage.setItem(STORAGE_KEY, String(org.id));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  return (
    <ActiveOrganizationContext.Provider
      value={{ activeOrganization, setActiveOrganization, organizations, isLoading }}
    >
      {children}
    </ActiveOrganizationContext.Provider>
  );
}

export function useActiveOrganization() {
  const ctx = React.useContext(ActiveOrganizationContext);
  if (!ctx) throw new Error('useActiveOrganization must be used within ActiveOrganizationProvider');
  return ctx;
}
